import React, { useState } from 'react';
import { X, Clock, RefreshCw, FileText, Brain, Target, MessageCircle, TrendingUp, ListChecks, Search, CalendarCheck, BarChart3, Flame, Sun, Snowflake, Shield, Mail, Send, MousePointerClick } from 'lucide-react';

interface EmailHowIWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'process' | 'behavior' | 'intelligence';

const processSteps = [
  {
    icon: Search,
    title: 'Receive & Research Leads',
    description: 'I receive your approved leads and research each prospect — their company, role, industry, and recent activity — so every email feels personally crafted and relevant.',
  },
  {
    icon: FileText,
    title: 'Generate Personalized Email',
    description: 'Using your approved templates and the lead\'s profile, I write a compelling pitch email with a personalized subject line and body — designed to cut through inbox noise.',
  },
  {
    icon: Clock,
    title: 'Optimize Subject Line & Send Time',
    description: 'I select the optimal send window based on the prospect\'s timezone and day of week, ensuring your email arrives when open rates are highest for their industry.',
  },
  {
    icon: Send,
    title: 'Send the Email',
    description: 'I deliver the email through your connected domain with proper DKIM/SPF authentication, maintaining sender reputation and maximizing deliverability.',
  },
  {
    icon: MousePointerClick,
    title: 'Track Engagement',
    description: 'I monitor opens, link clicks, and replies in real time. Each engagement signal feeds into my decision engine to determine the best next action for each prospect.',
  },
  {
    icon: CalendarCheck,
    title: 'Book Meeting or Schedule Follow-up',
    description: 'When a prospect replies with interest, I move to book a meeting. For unopened or unreplied emails, I queue the next follow-up in the sequence at the right interval.',
  },
  {
    icon: BarChart3,
    title: 'Log & Analyze Results',
    description: 'Every email is logged with detailed metrics — delivery status, open time, click data, reply content, and sentiment — giving you full campaign visibility at a glance.',
  },
];

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'behavior', label: 'Behavior', icon: Target },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

const EmailHowIWorkModal: React.FC<EmailHowIWorkModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('process');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Brain className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 font-display tracking-tight">How I Work</h3>
              <p className="text-sm text-gray-600 mt-1">Email outreach process, behavior rules and intelligence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'text-[#1875FF] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center space-x-2">
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'process' && <ProcessTab />}
          {activeTab === 'behavior' && <BehaviorTab />}
          {activeTab === 'intelligence' && <IntelligenceTab />}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

const ProcessTab = () => (
  <div className="space-y-4 animate-in slide-in-from-left duration-300">
    {processSteps.map((step, index) => {
      const Icon = step.icon;
      return (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-[#1875FF]/30 transition-all hover:shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 self-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg">
                <Icon className="text-white" size={24} />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-400 mb-1">
                STEP {index + 1}: <span className="text-gray-900">{step.title}</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const BehaviorTab = () => (
  <div className="space-y-6 animate-in slide-in-from-left duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Clock className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Send Windows</h4>
          <p className="text-gray-700 leading-relaxed">
            I send emails during optimal business hours in the prospect's timezone — typically
            Tuesday through Thursday mornings when open rates peak. I avoid weekends, holidays,
            and late-evening sends to maintain professional timing and maximize engagement.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <RefreshCw className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Follow-up Cadence</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I follow a 5-step cold email sequence with smart spacing:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">1.</span>
              <span>Pitch email — compelling intro with personalized value proposition</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">2.</span>
              <span>Follow-up 1 — gentle reminder with a new angle or social proof</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">3.</span>
              <span>Follow-up 2 — value-driven touchpoint with a relevant insight</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">4.</span>
              <span>Follow-up 3 — brief and direct with a clear CTA</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">5.</span>
              <span>Follow-up 4 — break-up email, closing the loop professionally</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Configurable delay between each step (default: days apart)</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Shield className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Domain Health & Deliverability</h4>
          <p className="text-gray-700 leading-relaxed">
            I send from your connected domain with proper DKIM, SPF, and DMARC authentication.
            I manage sending volume carefully to protect your domain reputation, avoid spam triggers,
            and ensure high deliverability rates across all campaigns.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Mail className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Signature & Branding</h4>
          <p className="text-gray-700 leading-relaxed">
            Every email includes your configured professional signature and follows your preferred
            email layout. Consistent branding across all touchpoints builds trust and recognition
            with prospects throughout the outreach sequence.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">When I Stop Emailing</h4>
          <p className="text-gray-700 leading-relaxed">
            I stop emailing when: the prospect replies asking to unsubscribe, the full 5-email sequence
            is completed without engagement, an email bounces (invalid address), a meeting has been
            successfully booked, or the lead is marked as unqualified.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const IntelligenceTab = () => (
  <div className="space-y-6 animate-in slide-in-from-right duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <MessageCircle className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Reply Sentiment Analysis</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I analyze every email reply to classify intent and determine the right next step:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Interested:</strong> Asking questions, requesting a demo or meeting</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Info request:</strong> Wants more details, pricing, or case studies</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Redirect:</strong> Points to a colleague or different department</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Not interested:</strong> Declines, unsubscribes, or requests removal</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <TrendingUp className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Temperature Classification</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I classify leads based on email engagement signals:
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
              <Flame size={14} className="text-red-500" />
              Hot Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
              <Sun size={14} />
              Warm Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
              <Snowflake size={14} />
              Cold Lead
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Mail className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Subject Line Optimization</h4>
          <p className="text-gray-700 leading-relaxed">
            I track which subject lines drive the highest open rates across your campaigns.
            Over time, I learn patterns — personalization tokens, question formats, curiosity hooks —
            that resonate best with your target audience and apply those insights to future emails.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Pattern Learning</h4>
          <p className="text-gray-700 leading-relaxed">
            I continuously learn from campaign performance — which email templates get the best
            reply rates, what send times drive opens, and which follow-up angles convert. These
            insights are applied automatically to improve future outreach effectiveness.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default EmailHowIWorkModal;
